import { message } from "antd";
import { apiRoot } from "consts";
import bem from "easy-bem";
import { useDataQuery } from "hooks/useData";
import { useUser } from "hooks/useUser";
import React, { useCallback, useState } from "react";

import type {
  Sphere,
  SphereTopicSelectProps,
  Topic,
} from "./SphereTopicSelect.types";

import "./SphereTopicSelect.css";

const cn = bem("sphere-topic-select");

const HIGHLIGHT_DURATION_MS = 2000;

const SphereTopicSelect: React.FC<SphereTopicSelectProps> = ({
  selectedSphereIds,
  selectedTopicIds,
  onChange,
}) => {
  const { isContentEditor } = useUser();
  const spheres = useDataQuery<Sphere[]>("spheres");
  const fetchedTopics = useDataQuery<Topic[]>("topics");
  const [extraTopics, setExtraTopics] = useState<Topic[]>([]);
  const [newTopicTitleBySphere, setNewTopicTitleBySphere] = useState<
    Record<number, string>
  >({});
  const [creatingSphereId, setCreatingSphereId] = useState<number | null>(null);
  const [highlightedTopicId, setHighlightedTopicId] = useState<number | null>(
    null,
  );

  const topics = [...(fetchedTopics || []), ...extraTopics];

  const toggleSphere = useCallback(
    (sphereId: number) => {
      const next = selectedSphereIds.includes(sphereId)
        ? selectedSphereIds.filter((id) => id !== sphereId)
        : [...selectedSphereIds, sphereId];

      onChange({ sphereIds: next, topicIds: selectedTopicIds });
    },
    [selectedSphereIds, selectedTopicIds, onChange],
  );

  const toggleTopic = useCallback(
    (topic: Topic) => {
      const isChecked = selectedTopicIds.includes(topic.id);
      const nextTopicIds = isChecked
        ? selectedTopicIds.filter((id) => id !== topic.id)
        : [...selectedTopicIds, topic.id];
      // Снятие темы не трогает сферу — она остаётся выбранной, пока её не снимут
      // явно своим чекбоксом. Выбор темы, наоборот, всегда подтягивает сферу.
      const nextSphereIds =
        !isChecked && !selectedSphereIds.includes(topic.sphere)
          ? [...selectedSphereIds, topic.sphere]
          : selectedSphereIds;

      onChange({ sphereIds: nextSphereIds, topicIds: nextTopicIds });
    },
    [selectedSphereIds, selectedTopicIds, onChange],
  );

  const handleAddTopic = useCallback(
    async (sphereId: number) => {
      const title = (newTopicTitleBySphere[sphereId] || "").trim();

      if (!title) return;

      setCreatingSphereId(sphereId);
      try {
        const response = await fetch(`${apiRoot}topics/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title, sphere: sphereId }),
          credentials: "include",
        });
        const data = await response.json();

        if (!response.ok)
          throw new Error(data?.error || `HTTP ${response.status}`);

        setExtraTopics((prev) => [...prev, data]);
        onChange({
          sphereIds: selectedSphereIds.includes(data.sphere)
            ? selectedSphereIds
            : [...selectedSphereIds, data.sphere],
          topicIds: [...selectedTopicIds, data.id],
        });
        setNewTopicTitleBySphere((prev) => ({ ...prev, [sphereId]: "" }));
        setHighlightedTopicId(data.id);
        window.setTimeout(
          () =>
            setHighlightedTopicId((current) =>
              current === data.id ? null : current,
            ),
          HIGHLIGHT_DURATION_MS,
        );
      } catch (error) {
        message.error(
          error instanceof Error ? error.message : "Не удалось добавить тему",
        );
      } finally {
        setCreatingSphereId(null);
      }
    },
    [newTopicTitleBySphere, selectedSphereIds, selectedTopicIds, onChange],
  );

  if (!spheres || !fetchedTopics) return <div>Загрузка…</div>;

  return (
    <div className={cn()}>
      <div className={cn("tree")}>
        {spheres.map((sphere) => (
          <div
            key={sphere.id}
            className={cn("sphere-group")}
          >
            <label className={cn("sphere-row")}>
              <input
                type="checkbox"
                checked={selectedSphereIds.includes(sphere.id)}
                onChange={() => toggleSphere(sphere.id)}
              />
              <span>{sphere.title}</span>
            </label>
            <div className={cn("topic-list")}>
              {topics
                .filter((topic) => topic.sphere === sphere.id)
                .map((topic) => (
                  <label
                    key={topic.id}
                    className={cn("topic-row", {
                      highlighted: highlightedTopicId === topic.id,
                    })}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTopicIds.includes(topic.id)}
                      onChange={() => toggleTopic(topic)}
                    />
                    <span>{topic.title}</span>
                  </label>
                ))}
              {isContentEditor && (
                <div className={cn("add-topic-row")}>
                  <input
                    className={cn("add-topic-title")}
                    placeholder="Новая тема…"
                    value={newTopicTitleBySphere[sphere.id] || ""}
                    onChange={(e) =>
                      setNewTopicTitleBySphere((prev) => ({
                        ...prev,
                        [sphere.id]: e.target.value,
                      }))
                    }
                  />
                  <button
                    type="button"
                    disabled={
                      creatingSphereId === sphere.id ||
                      !(newTopicTitleBySphere[sphere.id] || "").trim()
                    }
                    onClick={() => handleAddTopic(sphere.id)}
                  >
                    + Добавить тему
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SphereTopicSelect;
